import { DatePicker, TimeRangePickerProps } from "antd";
import { DateFilterMode } from "components/UI/Table/types/table.ts";
import dayjs, { Dayjs } from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";

const { RangePicker } = DatePicker;

// Only exposes the picker panel for the units the caller wants selectable — anything finer is
// clamped automatically by the caller (e.g. seconds forced to 00/59 in "minute" mode), same as
// "date" mode already clamps time-of-day to 00:00:00 / 23:59:59.
const SHOW_TIME_BY_MODE: Record<DateFilterMode, React.ComponentProps<typeof RangePicker>["showTime"]> = {
    date: false,
    hour: { format: "HH" },
    minute: { format: "HH:mm" },
    second: { format: "HH:mm:ss" },
};

// `showTime.format` only controls the picker panel's selectable columns — the input's displayed
// text has its own `format` prop, which otherwise defaults to full HH:mm:ss once showTime is set.
// The value formatted for the BE (REPORT_DATE_FORMAT_WITH_TIME, incl. clamped seconds) is separate
// and unaffected by this — it stays full precision regardless of what's shown here.
const DISPLAY_FORMAT_BY_MODE: Record<DateFilterMode, string> = {
    date: "YYYY-MM-DD",
    hour: "YYYY-MM-DD HH",
    minute: "YYYY-MM-DD HH:mm",
    second: "YYYY-MM-DD HH:mm:ss",
};

interface DateRangePickerProps {
    value?: [Dayjs | null, Dayjs | null];
    onChange?: (range: [Dayjs | null, Dayjs | null]) => void;
    allowClear?: boolean;
    dateFilterMode?: DateFilterMode;
}

export const DateRangePicker = ({
    value,
    onChange,
    allowClear = false,
    dateFilterMode = "date",
}: DateRangePickerProps) => {
    const { t } = usePaldockTranslation("ui.date-range-picker");

    const handleChange: TimeRangePickerProps["onChange"] = (dates) => {
        if (!dates) {
            onChange?.([null, null]);
            return;
        }
        onChange?.([dates[0], dates[1]]);
    };

    const presets: TimeRangePickerProps["presets"] = [
        { label: t("today"), value: [dayjs().startOf("day"), dayjs().endOf("day")] },
        {
            label: t("yesterday"),
            value: [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
        },
        { label: t("this_week"), value: [dayjs().startOf("week"), dayjs().endOf("day")] },
        {
            label: t("last_week"),
            value: [dayjs().subtract(1, "week").startOf("week"), dayjs().subtract(1, "week").endOf("week")],
        },
        { label: t("this_month"), value: [dayjs().startOf("month"), dayjs().endOf("day")] },
        {
            label: t("last_month"),
            value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")],
        },
        { label: t("last_7_days"), value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")] },
        { label: t("last_30_days"), value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
        { label: t("last_90_days"), value: [dayjs().subtract(89, "day").startOf("day"), dayjs().endOf("day")] },
        {
            label: t("last_12_months"),
            value: [dayjs().subtract(1, "year").add(1, "day").startOf("day"), dayjs().endOf("day")],
        },
    ];

    return (
        <RangePicker
            value={value}
            presets={presets}
            onChange={handleChange}
            allowClear={allowClear}
            showTime={SHOW_TIME_BY_MODE[dateFilterMode]}
            format={DISPLAY_FORMAT_BY_MODE[dateFilterMode]}
        />
    );
};
